import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { KeyValueFieldArray } from '@/components/key-value-field-array';
import { RequiredLabel } from '@/components/required-label';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { useFormikError } from '@/lib/error';

interface MCPFormValues {
  url?: string;
  timeout?: number;
  client_session_timeout_seconds?: number;
  headers?: { uid?: string; key?: string; value?: string }[];
  query_params?: { uid?: string; key?: string; value?: string }[];
}

type Props = {
  idPrefix?: string;
  isPending?: boolean;
};

export function MCPFields({ idPrefix = 'tool', isPending = false }: Props) {
  const { getFieldProps, values } = useFormikContext<MCPFormValues>();

  const hasError = useFormikError();

  return (
    <>
      <div className="space-y-2">
        <Field aria-invalid={hasError('url')}>
          <RequiredLabel htmlFor={`${idPrefix}-url`}>URL</RequiredLabel>
          <Input
            id={`${idPrefix}-url`}
            placeholder="https://your-mcp-server.com/sse"
            {...getFieldProps('url')}
            disabled={isPending}
            className="font-mono text-sm"
            aria-invalid={hasError('url')}
          />
          <ErrorField name="url" />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Field aria-invalid={hasError('timeout')}>
            <RequiredLabel htmlFor={`${idPrefix}-timeout`}>Timeout (ms)</RequiredLabel>
            <Input
              id={`${idPrefix}-timeout`}
              type="number"
              placeholder="5000"
              {...getFieldProps('timeout')}
              disabled={isPending}
              aria-invalid={hasError('timeout')}
            />
            <ErrorField name="timeout" />
          </Field>
        </div>

        <div className="space-y-2">
          <Field aria-invalid={hasError('client_session_timeout_seconds')}>
            <RequiredLabel htmlFor={`${idPrefix}-session-timeout`}>
              Session Timeout (ms)
            </RequiredLabel>
            <Input
              id={`${idPrefix}-session-timeout`}
              type="number"
              placeholder="5000"
              {...getFieldProps('client_session_timeout_seconds')}
              disabled={isPending}
              aria-invalid={hasError('client_session_timeout_seconds')}
            />
            <ErrorField name="client_session_timeout_seconds" />
          </Field>
        </div>
      </div>

      <KeyValueFieldArray
        name="headers"
        title="HTTP Headers"
        description="Custom HTTP headers to send with each request."
        items={values.headers ?? []}
        appearance="compact"
      />

      <KeyValueFieldArray
        name="query_params"
        title="Query Parameters"
        description="Custom query parameters to append to the URL."
        items={values.query_params ?? []}
        appearance="compact"
      />
    </>
  );
}
